import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import EnhancedFilePreview from "@/components/FilePreview";
import DocumentPreviewNavbar from "@/components/DocumentPriviewNavbar";
import { S3_BUCKET_LINK } from "@/components/constants";
import { getEnhancedFileMetadata } from "@/services/s3-file";
import { requireAuth } from "@/helpers/auth";

interface FileMetadata {
  contentType: string;
  contentLength: number;
  lastModified?: Date;
  etag?: string;
}

// Define search params interface
interface DocumentSearchParams {
  fileUrl: string;
  fileName?: string;
  fileSize?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  description?: string;
}

export const Route = createFileRoute("/document-viewer")({
  beforeLoad: requireAuth,
  component: StandaloneDocumentPreviewPage,
  validateSearch: (search: Record<string, unknown>): DocumentSearchParams => {
    return {
      fileUrl: (search.fileUrl as string) || "",
      fileName: search.fileName as string,
      fileSize: search.fileSize as string,
      uploadedBy: search.uploadedBy as string,
      uploadedAt: search.uploadedAt as string,
      description: search.description as string,
    };
  },
});

function StandaloneDocumentPreviewPage() {
  const search = useSearch({ from: "/document-viewer" });
  const [metadata, setMetadata] = useState<FileMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load file metadata when component mounts
  useEffect(() => {
    if (search.fileUrl) {
      setIsLoading(true);
      getEnhancedFileMetadata(search.fileUrl)
        .then((fileMetadata) => {
          setMetadata(fileMetadata);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error loading file metadata:", error);
          setMetadata(null);
          setIsLoading(false);
        });
    }
  }, [search.fileUrl]);

  // Handle missing fileUrl
  if (!search.fileUrl) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Custom Navbar */}
        <DocumentPreviewNavbar fileName="ບໍ່ພົບເອກະສານ" />

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">ບໍ່ພົບເອກະສານ</h1>
            <p className="text-gray-600 mb-4">
              ກະລຸນາກວດສອບລິ້ງທີ່ທ່ານກົດເຂົ້າມາ
            </p>
            <Button
              variant="outline"
              onClick={() => window.close()}
              className="inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              ປິດໜ້າຕ່າງ
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const downloadUrl = `${S3_BUCKET_LINK}${search.fileUrl}`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Custom Navbar for Standalone Document Preview */}
      <DocumentPreviewNavbar
        fileName={search.fileName || "ເອກະສານ"}
        downloadUrl={downloadUrl}
      />

      {/* Document Preview Content */}
      <main className="flex-1 py-6">
        <div className="container mx-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-lg">Loading file ...</div>
            </div>
          ) : (
            <EnhancedFilePreview
              fileUrl={search.fileUrl}
              fileName={search.fileName}
              metadata={metadata}
              showMetadata={true}
              showControls={true}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default StandaloneDocumentPreviewPage;
